import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, ProgressBar } from '@quality24/design-system';
import { SurveyHook } from '@/inpatient-patient-pwa/hooks/useSurvey/types';
import styles from './SurveyNavigation.module.scss';

export interface Props {
  className?: string;
  navigation: Pick<
    SurveyHook['nav'],
    | 'hasNext'
    | 'hasPrevious'
    | 'next'
    | 'previous'
    | 'submit'
    | 'canSkip'
    | 'canNext'
    | 'totalPages'
    | 'currentPageIndex'
  >;
}

const SurveyNavigation: React.FunctionComponent<Props> = ({
  className,
  navigation,
}) => {
  const { t } = useTranslation();
  const handleOnSkip = React.useCallback(() => {
    if (navigation.hasNext) {
      navigation.next();
      return window.scrollTo(0, 0);
    }
    return navigation.submit();
  }, [navigation]);

  const handleNext = React.useCallback(() => {
    navigation.next();
    return window.scrollTo(0, 0);
  }, [navigation]);

  return (
    <div className={classNames(styles.wrapper, className)}>
      <div className={styles.progressBar}>
        <ProgressBar
          steps={navigation.totalPages}
          currentStep={navigation.currentPageIndex}
          label={t('survey.nav.progress')}
        />
      </div>
      <div className={styles.buttonsWrapper}>
        <Button
          variant="link"
          onClick={handleOnSkip}
          size="lg"
          disabled={!navigation.canSkip}
        >
          {t('common.notApplicable')}
        </Button>

        {navigation.hasNext && (
          <Button onClick={handleNext} size="lg" disabled={!navigation.canNext}>
            {t('common.next')}
          </Button>
        )}

        {!navigation.hasNext && (
          <Button
            onClick={() => navigation.submit()}
            size="lg"
            disabled={!navigation.canNext}
          >
            {t('common.send')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SurveyNavigation;
